import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { accountApi } from '../api/account';
import { cartApi, paymentsApi, newIdempotencyKey } from '../api/cart';
import { contentApi } from '../api/content';
import type { AddressInputDto, CheckoutDto, CheckoutResultDto, PaymentCardInputDto, PayTrBinDetailDto, PaymentMethodType } from '../api/types';
import { Field, FormErrors, PageLoader, Spinner } from '../components/ui';
import { Seo } from '../components/Seo';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { paymentMethodLabels } from '../lib/labels';
import { money } from '../lib/format';
import { trackBeginCheckout } from '../lib/analytics';
import { TURKEY_CITIES, getDistrictsForCity } from '../lib/turkeyLocations';
import { SearchableSelect } from '../components/SearchableSelect';
import { shippingLocationsApi } from '../api';
import { ShieldCheck, Lock, CreditCard, Sparkles, X } from 'lucide-react';

/* ------------------------------------------------------------------ *
 *  Odeme akisi: adres → kargo → odeme → onay.
 *
 *  Checkout istegi `Idempotency-Key` ile gonderilir. Anahtar bilesen
 *  omru boyunca sabittir; ag koparsa ayni anahtarla tekrar denenir ve
 *  ikinci siparis olusmaz.
 * ------------------------------------------------------------------ */

const emptyAddress: AddressInputDto = {
  firstName: '',
  lastName: '',
  phone: '',
  addressLine1: '',
  district: '',
  city: '',
  countryCode: 'TR',
  isCorporate: false,
};

export function CheckoutPage() {
  const cart = useCart();
  const toast = useToast();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const idempotencyKey = useRef(newIdempotencyKey());

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [savedAddressId, setSavedAddressId] = useState<number | null>(null);
  const [shippingAddress, setShippingAddress] = useState<AddressInputDto>(emptyAddress);
  const [billingSame, setBillingSame] = useState(true);
  const [billingAddress, setBillingAddress] = useState<AddressInputDto>(emptyAddress);
  const [shippingRateId, setShippingRateId] = useState<number | null>(1);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType>('CreditCard');
  
  // PayTR Direkt API Kart Bilgileri & Taksit
  const [cardOwner, setCardOwner] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiryMonth, setCardExpiryMonth] = useState('12');
  const [cardExpiryYear, setCardExpiryYear] = useState('28');
  const [cardCvv, setCardCvv] = useState('');
  const [installmentCount, setInstallmentCount] = useState<number>(1);
  const [binDetail, setBinDetail] = useState<PayTrBinDetailDto | null>(null);
  const [binLoading, setBinLoading] = useState(false);

  const [requiresInvoice, setRequiresInvoice] = useState(false);
  const [acceptsTerms, setAcceptsTerms] = useState(false);
  const [confirmsAge] = useState(false);
  const [createAccount, setCreateAccount] = useState(false);
  const [customerNote, setCustomerNote] = useState('');
  const [paytrResult, setPaytrResult] = useState<CheckoutResultDto | null>(null);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const addresses = useQuery({
    queryKey: ['account', 'addresses'],
    queryFn: () => accountApi.addresses(),
    enabled: isAuthenticated,
  });

  const shippingOptions = useQuery({
    queryKey: ['cart', 'shipping-options'],
    queryFn: () => cartApi.shippingOptions(),
    enabled: true, // Adres secilmeden de genel kargo secenekleri dogrudan getirilir
  });

  // Kargo secenekleri gelince ilk secenek otomatik secilir
  useEffect(() => {
    if (shippingRateId === null && shippingOptions.data && shippingOptions.data.length > 0) {
      setShippingRateId(shippingOptions.data[0].shippingRateId);
    }
  }, [shippingOptions.data, shippingRateId]);

  const settings = useQuery({
    queryKey: ['content', 'settings'],
    queryFn: () => contentApi.publicSettings(),
    staleTime: 5 * 60_000,
  });

  const availableMethods = useMemo<PaymentMethodType[]>(() => {
    const list: PaymentMethodType[] = [];
    const cc = settings.data?.['payment.credit_card_enabled'];
    const bt = settings.data?.['payment.bank_transfer_enabled'];
    const cod = settings.data?.['payment.cod_enabled'];

    if (cc === undefined || cc === null || String(cc).toLowerCase() !== 'false') {
      list.push('CreditCard');
    }
    if (String(bt).toLowerCase() === 'true') {
      list.push('BankTransfer');
    }
    if (String(cod).toLowerCase() === 'true') {
      list.push('CashOnDelivery');
    }
    return list.length > 0 ? list : ['CreditCard'];
  }, [settings.data]);

  useEffect(() => {
    if (!availableMethods.includes(paymentMethod)) {
      setPaymentMethod(availableMethods[0]);
    }
  }, [availableMethods, paymentMethod]);

  // Analytics: Checkout started event
  useEffect(() => {
    if (cart.cart && cart.cart.items.length > 0) {
      trackBeginCheckout({
        total: cart.cart.grandTotal,
        items: cart.cart.items.map((i) => ({
          id: i.productId,
          name: i.productTitle,
          price: i.unitPrice,
          quantity: i.quantity,
          variant: i.variantTitle || undefined,
        })),
      });
    }
  }, [cart.cart?.publicId]);

  // Uye girisi varsa e-posta hesaptan gelir.
  useEffect(() => {
    if (user?.email) setEmail(user.email);
    if (user?.phoneNumber) setPhone(user.phoneNumber);
    if (user && !cardOwner) {
      const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
      if (name) setCardOwner(name.toUpperCase());
    }
  }, [user]);

  // URL'den dönen PayTR hata veya iptal parametrelerini yakalama
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hata = params.get('hata');
    if (hata === 'paytr_failed') {
      toast.error('Ödeme işlemi tamamlanamadı veya reddedildi. Lütfen kart bilgilerinizi kontrol edip tekrar deneyiniz.');
    } else if (hata === '3d_cancelled') {
      toast.info('3D Secure ödeme işlemi iptal edildi. Sepetiniz korunmaktadır.');
    }
  }, [toast]);

  // Teslimat ismi girilince kart sahibi alanını doldur
  useEffect(() => {
    if (!cardOwner && (shippingAddress.firstName || shippingAddress.lastName)) {
      const name = `${shippingAddress.firstName} ${shippingAddress.lastName}`.trim();
      if (name) setCardOwner(name.toUpperCase());
    }
  }, [shippingAddress.firstName, shippingAddress.lastName, cardOwner]);

  // Kart numarası girişi ve anlık BIN sorgusu
  const handleCardNumberChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 16);
    const formatted = digits.replace(/(\d{4})(?=\d)/g, '$1 ');
    setCardNumber(formatted);

    if (digits.length >= 6) {
      const bin = digits.slice(0, 8);
      if (binDetail?.binNumber !== bin && !binLoading) {
        setBinLoading(true);
        paymentsApi
          .getBinDetail(bin)
          .then((res) => {
            setBinDetail(res);
            if (!res.allowsInstallment) {
              setInstallmentCount(1);
            }
          })
          .catch(() => {})
          .finally(() => setBinLoading(false));
      }
    } else {
      setBinDetail(null);
    }
  };

  // Hızlı test kartı doldurma
  const fillTestCard = (type: 'troy' | 'visa') => {
    if (type === 'troy') {
      handleCardNumberChange('9792030394440796');
      setCardOwner('TEST KARTI');
      setCardExpiryMonth('12');
      setCardExpiryYear('30');
      setCardCvv('000');
    } else {
      // PayTR resmi dokümandaki test kartı (Mastercard)
      handleCardNumberChange('5406675406675403');
      setCardOwner('TEST KARTI');
      setCardExpiryMonth('12');
      setCardExpiryYear('30');
      setCardCvv('000');
    }
    toast.success('Test kart bilgileri forma dolduruldu.');
  };

  // Varsayilan teslimat adresi otomatik secilir.
  useEffect(() => {
    if (savedAddressId === null && addresses.data && addresses.data.length > 0) {
      const preferred = addresses.data.find((address) => address.isDefaultShipping);
      setSavedAddressId((preferred ?? addresses.data[0]).id);
    }
  }, [addresses.data, savedAddressId]);

  /** Adres degisince sepete bildirilir; kargo ucreti ve vergi yeniden hesaplanir. */
  const syncShipping = useMutation({
    mutationFn: (rateId: number | null) =>
      cart.setShipping({
        shippingRateId: rateId,
        shippingAddressId: savedAddressId,
        shippingAddress: savedAddressId === null ? shippingAddress : null,
      }),
    onError: (error) => toast.fromError(error, 'Teslimat bilgisi kaydedilemedi.'),
  });

  const checkout = useMutation({
    mutationFn: () => {
      // Kredi kartı seçildiyse temel kart doğrulaması
      if (paymentMethod === 'CreditCard') {
        const cleanDigits = cardNumber.replace(/\D/g, '');
        if (cleanDigits.length < 15) {
          throw new Error('Lütfen 16 haneli kredi kartı numaranızı eksiksiz giriniz.');
        }
        if (!cardOwner.trim()) {
          throw new Error('Lütfen kart üzerindeki isim ve soyismi giriniz.');
        }
        if (!cardExpiryMonth || !cardExpiryYear) {
          throw new Error('Lütfen kart son kullanma tarihini giriniz.');
        }
        if (cardCvv.replace(/\D/g, '').length < 3) {
          throw new Error('Lütfen 3 haneli güvenlik kodunu (CVV) giriniz.');
        }
      }

      const cardPayload: PaymentCardInputDto | null =
        paymentMethod === 'CreditCard'
          ? {
              ccOwner: cardOwner.trim(),
              cardNumber: cardNumber.replace(/\D/g, ''),
              expiryMonth: cardExpiryMonth.padStart(2, '0'),
              expiryYear: cardExpiryYear.length === 4 ? cardExpiryYear.slice(2) : cardExpiryYear,
              cvv: cardCvv.trim(),
              installmentCount: installmentCount,
              cardType: binDetail?.cardFamily || '',
            }
          : null;

      const dto: CheckoutDto = {
        email: isAuthenticated ? null : email,
        phone: phone || null,
        shippingAddressId: savedAddressId,
        shippingAddress: savedAddressId === null ? shippingAddress : null,
        billingSameAsShipping: billingSame,
        billingAddressId: null,
        billingAddress: billingSame ? null : billingAddress,
        shippingRateId: shippingRateId ?? 0,
        paymentMethod,
        installment: installmentCount,
        customerNote: customerNote || null,
        requiresInvoice,
        acceptsTerms,
        createAccount: !isAuthenticated && createAccount,
        confirmsAge,
        card: cardPayload,
      };

      return cartApi.checkout(dto, idempotencyKey.current);
    },
    onSuccess: (result) => {
      // PayTR Direkt API 3D Secure Formu:
      // Tarayıcı üzerinden doğrudan https://www.paytr.com/odeme adresine auto-submit edilir.
      if (result.paymentFormHtml) {
        toast.info('PayTR 3D Secure güvenlik ekranına aktarılıyorsunuz...');
        const wrapper = document.createElement('div');
        wrapper.style.display = 'none';
        wrapper.innerHTML = result.paymentFormHtml;
        document.body.appendChild(wrapper);
        const form = wrapper.querySelector('form');
        if (form) {
          form.submit();
          return;
        }
      }

      // 3D Secure Simülasyonu veya Harici Yönlendirme
      if (result.requiresPaymentAction && result.paymentRedirectUrl) {
        toast.info('Güvenlik doğrulama ekranına yönlendiriliyorsunuz...');
        window.location.href = result.paymentRedirectUrl;
        return;
      }

      // PayTR iFrame Geriye Dönük Uyumluluk (Legacy Fallback)
      if (result.paymentIframeUrl) {
        setPaytrResult(result);
        setIframeLoading(true);
        setIsModalOpen(true);
        return;
      }

      // Havale, Kapıda Ödeme veya Doğrudan Onaylanan Sipariş
      cart.reload();
      navigate(`/siparis-tamam/${result.orderPublicId}`, { state: result, replace: true });
    },
    onError: (error) => toast.fromError(error, 'Sipariş oluşturulamadı.'),
  });

  // Modal kapatma / ödeme iptali
  const handleCloseModal = async () => {
    if (window.confirm('Ödeme penceresini kapatmak istediğinize emin misiniz? Sepetinizdeki ürünler korunacaktır.')) {
      const orderId = paytrResult?.orderPublicId;
      setIsModalOpen(false);
      setPaytrResult(null);
      if (orderId) {
        try {
          await paymentsApi.cancelDraft(orderId);
        } catch {
          // ignore
        }
      }
      toast.info('Ödeme işlemi iptal edildi. Sepetiniz korunuyor.');
    }
  };

  // PayTR iframe resizer betiğini dinamik yükle
  useEffect(() => {
    if (!paytrResult?.paymentIframeUrl) return;

    const scriptId = 'paytr-resizer-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://www.paytr.com/js/iframeResizer.min.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, [paytrResult]);

  // PayTR Sandbox iFrame mesaj dinleyicisi (anlık onay)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'PAYTR_PAYMENT_SUCCESS') {
        const orderId = event.data.orderPublicId || paytrResult?.orderPublicId;
        if (orderId) {
          setIsModalOpen(false);
          cart.reload();
          toast.success('Ödemeniz başarıyla alındı!');
          navigate(`/siparis-tamam/${orderId}`, {
            state: { ...paytrResult, orderPublicId: orderId, isPaid: true },
            replace: true,
          });
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [paytrResult, navigate, toast, cart]);

  // PayTR iFrame açıkken ödeme durumunu periyodik kontrol et (polling)
  useEffect(() => {
    if (!isModalOpen || !paytrResult?.orderPublicId) return;

    let isSubscribed = true;
    const interval = setInterval(async () => {
      try {
        const res = await paymentsApi.getStatus(paytrResult.orderPublicId);
        if (!isSubscribed) return;

        if (res.isPaid) {
          clearInterval(interval);
          setIsModalOpen(false);
          cart.reload();
          toast.success('Ödemeniz başarıyla alındı!');
          const finalOrderId = res.orderPublicId || paytrResult.orderPublicId;
          navigate(`/siparis-tamam/${finalOrderId}`, {
            state: {
              ...paytrResult,
              orderPublicId: finalOrderId,
              orderNumber: res.orderNumber || paytrResult.orderNumber,
              isPaid: true,
            },
            replace: true,
          });
        }
      } catch {
        // Ağ hatası durumunda sonraki döngüde tekrar dene
      }
    }, 2500);

    return () => {
      isSubscribed = false;
      clearInterval(interval);
    };
  }, [isModalOpen, paytrResult, navigate, toast, cart]);

  const lastCartData = useRef<typeof cart.cart>(null);
  if (cart.cart && cart.cart.items.length > 0) {
    lastCartData.current = cart.cart;
  }
  const data = cart.cart ?? lastCartData.current;
  const grandTotal = data?.amountDue ?? 0;

  const installmentOptions = useMemo(() => {
    const allowInst = binDetail?.allowsInstallment ?? true;
    if (!allowInst || settings.data?.['payment.paytr_no_installment'] === '1') {
      return [{ count: 1, label: 'Tek Çekim', monthly: grandTotal, total: grandTotal }];
    }
    const maxInst = Number(settings.data?.['payment.paytr_max_installment'] || 12);
    const options = [{ count: 1, label: 'Tek Çekim (Peşin)', monthly: grandTotal, total: grandTotal }];
    const counts = [2, 3, 6, 9, 12].filter((c) => (maxInst > 0 ? c <= maxInst : true));
    for (const c of counts) {
      options.push({
        count: c,
        label: `${c} Taksit`,
        monthly: Math.round((grandTotal / c) * 100) / 100,
        total: grandTotal,
      });
    }
    return options;
  }, [grandTotal, binDetail, settings.data]);

  if (cart.isLoading && !lastCartData.current) return <PageLoader />;

  if (!data || (data.items.length === 0 && !paytrResult)) {
    return (
      <div className="container-page py-16 text-center">
        <p className="text-ink-600">Sepetiniz boş.</p>
        <Link to="/urunler" className="btn-primary mt-4">
          Ürünlere göz at
        </Link>
      </div>
    );
  }

  const addressValid =
    savedAddressId !== null ||
    (shippingAddress.firstName &&
      shippingAddress.lastName &&
      shippingAddress.phone &&
      shippingAddress.addressLine1 &&
      shippingAddress.district &&
      shippingAddress.city);

  const cardValid =
    paymentMethod !== 'CreditCard' ||
    (cardNumber.replace(/\D/g, '').length >= 15 &&
      cardOwner.trim().length > 0 &&
      cardExpiryMonth.length > 0 &&
      cardExpiryYear.length > 0 &&
      cardCvv.replace(/\D/g, '').length >= 3);

  const canSubmit =
    Boolean(addressValid) &&
    shippingRateId !== null &&
    acceptsTerms &&
    (isAuthenticated || email.length > 0) &&
    cardValid &&
    data.canCheckout;

  return (
    <div className="container-page py-8">
      <Seo title="Güvenli Ödeme" noIndex />
      <h1 className="mb-6 text-2xl font-semibold tracking-tight text-ink-900">Ödeme</h1>

      <form
        className="grid gap-8 lg:grid-cols-[1fr_360px]"
        onSubmit={(event) => {
          event.preventDefault();
          checkout.mutate();
        }}
      >
        <div className="space-y-6">
          <FormErrors error={checkout.error} />

          {/* 1. Iletisim */}
          <section className="card p-5">
            <h2 className="mb-4 text-base font-semibold text-ink-900">1. İletişim</h2>

            {isAuthenticated ? (
              <p className="text-sm text-ink-600">
                {user?.fullName ?? user?.email} olarak sipariş veriyorsunuz.
              </p>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="E-posta" htmlFor="checkout-email" required>
                  <input
                    id="checkout-email"
                    type="email"
                    required
                    className="field"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </Field>
                <Field label="Telefon" htmlFor="checkout-phone">
                  <input
                    id="checkout-phone"
                    type="tel"
                    className="field"
                    value={phone}
                    onChange={(event) => setPhone(event.target.value)}
                  />
                </Field>
                <label className="flex items-center gap-2 text-sm text-ink-700 sm:col-span-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-ink-300 text-brand-600"
                    checked={createAccount}
                    onChange={(event) => setCreateAccount(event.target.checked)}
                  />
                  Siparişimle birlikte hesap oluştur
                </label>
              </div>
            )}
          </section>

          {/* 2. Teslimat adresi */}
          <section className="card p-5">
            <h2 className="mb-4 text-base font-semibold text-ink-900">2. Teslimat adresi</h2>

            {isAuthenticated && addresses.data && addresses.data.length > 0 ? (
              <div className="mb-4 space-y-2">
                {addresses.data.map((address) => (
                  <label
                    key={address.id}
                    className={`flex cursor-pointer gap-3 rounded-lg border p-3.5 text-sm ${
                      savedAddressId === address.id
                        ? 'border-ink-900 bg-ink-50'
                        : 'border-ink-200 hover:border-ink-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="address"
                      className="mt-0.5 h-4 w-4 border-ink-300 text-brand-600"
                      checked={savedAddressId === address.id}
                      onChange={() => {
                        setSavedAddressId(address.id);
                        setShippingRateId(null);
                      }}
                    />
                    <span className="min-w-0">
                      <span className="block font-medium text-ink-900">
                        {address.title ?? `${address.firstName} ${address.lastName}`}
                      </span>
                      <span className="block text-ink-600">
                        {address.addressLine1}
                        {address.addressLine2 ? `, ${address.addressLine2}` : ''} · {address.district} /{' '}
                        {address.city}
                      </span>
                      <span className="block text-xs text-ink-400">{address.phone}</span>
                    </span>
                  </label>
                ))}

                <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-ink-300 p-3.5 text-sm">
                  <input
                    type="radio"
                    name="address"
                    className="h-4 w-4 border-ink-300 text-brand-600"
                    checked={savedAddressId === null}
                    onChange={() => {
                      setSavedAddressId(null);
                      setShippingRateId(null);
                    }}
                  />
                  Yeni adres gir
                </label>
              </div>
            ) : null}

            {savedAddressId === null ? (
              <AddressForm
                value={shippingAddress}
                onChange={(next) => {
                  setShippingAddress(next);
                  setShippingRateId(null);
                }}
                idPrefix="ship"
              />
            ) : null}

          </section>

          {/* 3. Kargo */}
          <section className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-ink-900">3. Teslimat & Kargo</h2>
              <span className="text-xs text-ink-500 font-medium">Tüm Türkiye'ye Teslimat</span>
            </div>

            {(() => {
              const options =
                shippingOptions.data && shippingOptions.data.length > 0
                  ? shippingOptions.data
                  : [
                      {
                        shippingRateId: 1,
                        name: 'Sürat Kargo (Tüm Türkiye)',
                        description: 'Türkiye geneli tüm adreslere güvenli anlaşmalı teslimat',
                        price: 65,
                        currencyCode: 'TRY',
                        isFree: false,
                        minDeliveryDays: 1,
                        maxDeliveryDays: 3,
                      },
                    ];

              return (
                <div className="space-y-2">
                  {options.map((option) => {
                    const isSelected = (shippingRateId ?? 1) === option.shippingRateId;
                    return (
                      <label
                        key={option.shippingRateId}
                        className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3.5 text-sm transition-colors ${
                          isSelected
                            ? 'border-brand-600 bg-brand-50/40 text-brand-950 font-medium ring-1 ring-brand-500'
                            : 'border-ink-200 hover:border-ink-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="shipping"
                          className="h-4 w-4 border-ink-300 text-brand-600"
                          checked={isSelected}
                          onChange={() => {
                            setShippingRateId(option.shippingRateId);
                            syncShipping.mutate(option.shippingRateId);
                          }}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-ink-900">{option.name}</span>
                            <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                              Anlaşmalı Kargo
                            </span>
                          </div>
                          {option.description ? (
                            <span className="block text-xs text-ink-500">{option.description}</span>
                          ) : null}
                          <span className="block text-xs text-ink-400">
                            Tahmini {option.minDeliveryDays ?? 1}-{option.maxDeliveryDays ?? 3} iş günü içinde teslimat
                          </span>
                        </div>
                        <span className="font-semibold text-ink-900">
                          {option.isFree ? 'Ücretsiz' : money(option.price, option.currencyCode)}
                        </span>
                      </label>
                    );
                  })}
                </div>
              );
            })()}
          </section>

          {/* 4. Fatura */}
          <section className="card p-5">
            <h2 className="mb-4 text-base font-semibold text-ink-900">4. Fatura</h2>

            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-ink-300 text-brand-600"
                checked={billingSame}
                onChange={(event) => setBillingSame(event.target.checked)}
              />
              Fatura adresim teslimat adresimle aynı
            </label>

            {!billingSame ? (
              <div className="mt-4">
                <AddressForm value={billingAddress} onChange={setBillingAddress} idPrefix="bill" corporate />
              </div>
            ) : null}

            <label className="mt-3 flex items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-ink-300 text-brand-600"
                checked={requiresInvoice}
                onChange={(event) => setRequiresInvoice(event.target.checked)}
              />
              e-Fatura istiyorum
            </label>
          </section>

          {/* 5. Odeme */}
          <section className="card p-5">
            <h2 className="mb-4 text-base font-semibold text-ink-900">5. Ödeme yöntemi</h2>

            <div className="grid gap-2 sm:grid-cols-2">
              {availableMethods.map((method) => (
                <label
                  key={method}
                  className={`flex cursor-pointer items-center justify-between rounded-lg border p-3.5 text-sm ${
                    paymentMethod === method
                      ? 'border-ink-900 bg-ink-50 font-medium'
                      : 'border-ink-200 hover:border-ink-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      name="payment"
                      className="h-4 w-4 border-ink-300 text-brand-600"
                      checked={paymentMethod === method}
                      onChange={() => setPaymentMethod(method)}
                    />
                    <span>{paymentMethodLabels[method]}</span>
                  </div>
                  {method === 'CashOnDelivery' &&
                    settings.data?.['payment.cod_fee'] &&
                    Number(settings.data['payment.cod_fee']) > 0 && (
                      <span className="text-xs text-ink-500 font-normal">
                        +{money(Number(settings.data['payment.cod_fee']))}
                      </span>
                    )}
                </label>
              ))}
            </div>

            {paymentMethod === 'CreditCard' && (
              <div className="mt-4 rounded-2xl border border-brand-200 bg-brand-50/30 p-5 space-y-5">
                <div className="flex flex-wrap items-center justify-between gap-2 pb-3 border-b border-brand-100">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-emerald-600" />
                    <div>
                      <span className="font-semibold text-ink-900 text-sm block">
                        PayTR Direkt API Güvenli Kart Ödemesi
                      </span>
                      <span className="text-[11px] text-ink-500 block">
                        Doğrudan kart girişi & 3D Secure onay adımı
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-800">
                      🔒 256-Bit SSL
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-0.5 text-[11px] font-semibold text-blue-800">
                      3D Secure
                    </span>
                  </div>
                </div>

                {/* Test Kartı Hızlı Doldurma Yardımcısı */}
                <div className="flex flex-wrap items-center justify-between gap-2 bg-white/80 p-2.5 rounded-xl border border-brand-100 text-xs">
                  <span className="font-medium text-ink-600 flex items-center gap-1.5">
                    <Sparkles className="h-4 w-4 text-amber-500" />
                    <span>Hızlı Test Doldurma:</span>
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => fillTestCard('troy')}
                      className="px-2.5 py-1 rounded-lg bg-teal-50 border border-teal-200 text-teal-800 font-medium hover:bg-teal-100 transition-colors"
                    >
                      ⚡ PayTR Troy Test Kartı
                    </button>
                    <button
                      type="button"
                      onClick={() => fillTestCard('visa')}
                      className="px-2.5 py-1 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 font-medium hover:bg-blue-100 transition-colors"
                    >
                      ⚡ Test Mastercard Kartı
                    </button>
                  </div>
                </div>

                {/* Canlı Kredi Kartı Görsel Önizlemesi */}
                <div className="relative mx-auto w-full max-w-sm h-48 rounded-2xl p-5 text-white shadow-xl transition-all overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 border border-slate-700">
                  <div className="absolute -right-10 -bottom-10 w-44 h-44 rounded-full bg-brand-500/10 blur-2xl pointer-events-none" />
                  <div className="absolute -left-10 -top-10 w-44 h-44 rounded-full bg-indigo-500/10 blur-xl pointer-events-none" />

                  <div className="relative z-10 flex flex-col justify-between h-full">
                    <div className="flex items-center justify-between">
                      {/* Çip ve Temassız Simgesi */}
                      <div className="flex items-center gap-2.5">
                        <div className="w-10 h-7 rounded-sm bg-gradient-to-tr from-amber-400 to-amber-200 border border-amber-500/80 flex items-center justify-center shadow-inner">
                          <div className="w-7 h-4 border border-amber-700/40 rounded-2xs" />
                        </div>
                        <svg className="w-4 h-4 text-slate-300 opacity-80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M8.5 16.5a5 5 0 0 1 0-9" />
                          <path d="M12 19a9 9 0 0 0 0-14" />
                          <path d="M15.5 21.5a13 13 0 0 0 0-19" />
                        </svg>
                      </div>

                      {/* Banka ve Kart Programı */}
                      <div className="text-right">
                        {binDetail?.bankName ? (
                          <span className="block text-[11px] font-bold tracking-wider text-slate-200 uppercase">
                            {binDetail.bankName}
                          </span>
                        ) : null}
                        {binDetail?.cardFamily && binDetail.cardFamily !== 'none' ? (
                          <span className="inline-block mt-0.5 px-2 py-0.5 rounded bg-amber-400/20 text-[10px] font-extrabold text-amber-300 tracking-wide uppercase">
                            {binDetail.cardFamily}
                          </span>
                        ) : null}
                      </div>
                    </div>

                    {/* Kart Numarası */}
                    <div className="font-mono text-lg sm:text-xl tracking-[0.18em] font-semibold text-slate-100 select-all">
                      {cardNumber || '•••• •••• •••• ••••'}
                    </div>

                    {/* Kart Sahibi, Son Kullanma ve Şema Logosu */}
                    <div className="flex items-end justify-between text-xs">
                      <div className="min-w-0 max-w-[170px]">
                        <span className="block text-[9px] uppercase tracking-wider text-slate-400">Kart Sahibi</span>
                        <span className="font-medium tracking-wide text-slate-100 uppercase truncate block">
                          {cardOwner || 'AD SOYAD'}
                        </span>
                      </div>
                      <div>
                        <span className="block text-[9px] uppercase tracking-wider text-slate-400">Son Kul.</span>
                        <span className="font-mono font-medium text-slate-100">
                          {cardExpiryMonth}/{cardExpiryYear}
                        </span>
                      </div>
                      <div className="flex items-center">
                        {(() => {
                          const schema = (binDetail?.cardAssociation || '').toUpperCase();
                          if (schema === 'TROY') {
                            return (
                              <span className="px-2 py-0.5 rounded bg-teal-500/20 text-teal-300 font-black text-[11px] tracking-wider border border-teal-400/40">
                                TROY
                              </span>
                            );
                          }
                          if (schema === 'VISA') {
                            return (
                              <span className="px-2 py-0.5 rounded bg-blue-500/20 text-blue-300 font-black italic text-xs tracking-wider border border-blue-400/40">
                                VISA
                              </span>
                            );
                          }
                          if (schema === 'MASTERCARD') {
                            return (
                              <div className="flex items-center -space-x-1.5">
                                <div className="w-5 h-5 rounded-full bg-rose-500/90 shadow" />
                                <div className="w-5 h-5 rounded-full bg-amber-400/90 shadow" />
                              </div>
                            );
                          }
                          if (schema === 'AMEX') {
                            return (
                              <span className="px-2 py-0.5 rounded bg-sky-600/30 text-sky-200 font-bold text-[10px] tracking-tight border border-sky-400/30">
                                AMEX
                              </span>
                            );
                          }
                          return <CreditCard className="w-6 h-6 text-slate-400" />;
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Kart Giriş Form Alanları */}
                <div className="space-y-3.5 bg-white p-4 rounded-xl border border-ink-200">
                  <div>
                    <label className="block text-xs font-semibold text-ink-700 mb-1" htmlFor="cardOwner">
                      Kart Sahibi Adı Soyadı
                    </label>
                    <input
                      id="cardOwner"
                      type="text"
                      className="field"
                      placeholder="Ahmet Yılmaz"
                      value={cardOwner}
                      onChange={(e) => setCardOwner(e.target.value.toUpperCase())}
                      maxLength={50}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-ink-700 mb-1" htmlFor="cardNumber">
                      Kart Numarası
                    </label>
                    <div className="relative">
                      <input
                        id="cardNumber"
                        type="text"
                        className="field font-mono pr-20"
                        placeholder="•••• •••• •••• ••••"
                        value={cardNumber}
                        onChange={(e) => handleCardNumberChange(e.target.value)}
                        maxLength={19}
                        required
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 pointer-events-none">
                        {binLoading ? (
                          <Spinner className="h-4 w-4 text-brand-600" />
                        ) : binDetail?.cardAssociation ? (
                          <span className="text-[10px] font-bold text-ink-600 px-1.5 py-0.5 rounded bg-ink-100">
                            {binDetail.cardAssociation}
                          </span>
                        ) : (
                          <CreditCard className="h-4 w-4 text-ink-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-ink-700 mb-1">
                        Son Kullanma Tarihi
                      </label>
                      <div className="grid grid-cols-2 gap-1.5">
                        <select
                          className="field px-2 text-xs"
                          value={cardExpiryMonth}
                          onChange={(e) => setCardExpiryMonth(e.target.value)}
                        >
                          {['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'].map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </select>
                        <select
                          className="field px-2 text-xs"
                          value={cardExpiryYear}
                          onChange={(e) => setCardExpiryYear(e.target.value)}
                        >
                          {Array.from({ length: 16 }, (_, i) => String((new Date().getFullYear() + i) % 100).padStart(2, '0')).map((y) => (
                            <option key={y} value={y}>
                              20{y}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs font-semibold text-ink-700" htmlFor="cardCvv">
                          Güvenlik Kodu (CVV)
                        </label>
                        <span className="text-[10px] text-ink-400">Kart arkası</span>
                      </div>
                      <input
                        id="cardCvv"
                        type="password"
                        className="field font-mono text-center tracking-widest"
                        placeholder="•••"
                        value={cardCvv}
                        onChange={(e) => setCardCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        maxLength={4}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Taksit Seçenekleri */}
                <div className="rounded-xl border border-ink-200 bg-white p-4">
                  <div className="flex items-center justify-between mb-3 pb-2 border-b border-ink-100">
                    <span className="font-semibold text-ink-900 text-xs">
                      Taksit Seçenekleri
                    </span>
                    {binDetail?.cardFamily && binDetail.cardFamily !== 'none' ? (
                      <span className="text-[11px] font-semibold text-brand-600 bg-brand-50 px-2 py-0.5 rounded-full">
                        {binDetail.bankName ? `${binDetail.bankName} — ` : ''}{binDetail.cardFamily.toUpperCase()}
                      </span>
                    ) : (
                      <span className="text-[11px] text-ink-400">Tüm kartlara taksit desteği</span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {installmentOptions.map((opt) => {
                      const isSelected = installmentCount === opt.count;
                      return (
                        <label
                          key={opt.count}
                          className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                            isSelected
                              ? 'border-brand-600 bg-brand-50/50 text-brand-950 font-medium ring-1 ring-brand-500'
                              : 'border-ink-200 hover:border-ink-300'
                          }`}
                        >
                          <div className="flex items-center gap-2.5">
                            <input
                              type="radio"
                              name="installmentPlan"
                              checked={isSelected}
                              onChange={() => setInstallmentCount(opt.count)}
                              className="h-4 w-4 border-ink-300 text-brand-600"
                            />
                            <div>
                              <span className="block text-xs font-semibold text-ink-900">
                                {opt.label}
                              </span>
                              {opt.count > 1 ? (
                                <span className="block text-[11px] text-ink-500">
                                  {opt.count} x {money(opt.monthly, data.currencyCode)}
                                </span>
                              ) : (
                                <span className="block text-[11px] text-emerald-600 font-medium">
                                  Komisyonsuz Tek Çekim
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="font-semibold text-xs text-ink-900">
                            {money(opt.total, data.currencyCode)}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-center gap-2 text-[11px] text-ink-500 leading-tight">
                  <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0" />
                  <span>
                    Kart bilgileriniz doğrudan PayTR 3D Secure sistemine iletilir ve sunucularımızda saklanmaz. Ödeme onaylanmadan sipariş oluşturulmaz.
                  </span>
                </div>
              </div>
            )}

            {paymentMethod === 'BankTransfer' ? (
              <div className="mt-3 rounded-lg border border-brand-200 bg-brand-50/70 p-4 text-sm text-brand-900 space-y-2">
                <p className="font-semibold text-brand-950">Havale / EFT Hesap Bilgileri</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  {settings.data?.['payment.bank_transfer_bank_name'] && (
                    <div>
                      <span className="text-brand-600 block">Banka:</span>
                      <span className="font-medium text-brand-900">
                        {settings.data['payment.bank_transfer_bank_name']}
                      </span>
                    </div>
                  )}
                  {settings.data?.['payment.bank_transfer_account_holder'] && (
                    <div>
                      <span className="text-brand-600 block">Hesap Sahibi:</span>
                      <span className="font-medium text-brand-900">
                        {settings.data['payment.bank_transfer_account_holder']}
                      </span>
                    </div>
                  )}
                  {settings.data?.['payment.bank_transfer_iban'] && (
                    <div className="sm:col-span-2">
                      <span className="text-brand-600 block">IBAN:</span>
                      <span className="font-mono font-bold tracking-wider text-brand-900">
                        {settings.data['payment.bank_transfer_iban']}
                      </span>
                    </div>
                  )}
                </div>
                {settings.data?.['payment.bank_transfer_description'] && (
                  <p className="text-xs text-brand-700 pt-1 border-t border-brand-200/60">
                    ℹ️ {settings.data['payment.bank_transfer_description']}
                  </p>
                )}
              </div>
            ) : null}

            <Field label="Sipariş notu" htmlFor="checkout-note" className="mt-4">
              <textarea
                id="checkout-note"
                className="field min-h-20"
                maxLength={1000}
                value={customerNote}
                onChange={(event) => setCustomerNote(event.target.value)}
              />
            </Field>
          </section>
        </div>

        {/* Ozet ve onay */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="card p-5">
            <h2 className="text-base font-semibold text-ink-900">Özet</h2>

            <ul className="mt-4 space-y-3">
              {data.items.map((line) => (
                <li key={line.id} className="flex gap-3 text-sm">
                  <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-ink-50">
                    {line.imageUrl ? (
                      <img src={line.imageUrl} alt="" className="h-full w-full object-cover" />
                    ) : null}
                    <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-ink-800 px-1 text-[10px] text-white">
                      {line.quantity}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-ink-800">{line.productTitle}</p>
                    {line.variantTitle ? (
                      <p className="truncate text-xs text-ink-500">{line.variantTitle}</p>
                    ) : null}
                  </div>
                  <span className="text-ink-900">{money(line.lineTotal, data.currencyCode)}</span>
                </li>
              ))}
            </ul>

            <dl className="mt-5 space-y-2 border-t border-ink-200 pt-4 text-sm">
              <div className="flex justify-between">
                <dt className="text-ink-600">Ara toplam</dt>
                <dd>{money(data.subTotal, data.currencyCode)}</dd>
              </div>
              {data.discountTotal > 0 ? (
                <div className="flex justify-between text-emerald-700">
                  <dt>İndirim</dt>
                  <dd>− {money(data.discountTotal, data.currencyCode)}</dd>
                </div>
              ) : null}
              <div className="flex justify-between">
                <dt className="text-ink-600">Kargo</dt>
                <dd>{money(data.shippingTotal, data.currencyCode)}</dd>
              </div>
              {data.taxLines.map((tax) => (
                <div key={tax.title} className="flex justify-between text-ink-500">
                  <dt>
                    {tax.title} (%{tax.rate}){tax.isIncludedInPrice ? ' — dahil' : ''}
                  </dt>
                  <dd>{money(tax.amount, data.currencyCode)}</dd>
                </div>
              ))}
              {data.giftCardTotal > 0 ? (
                <div className="flex justify-between text-emerald-700">
                  <dt>Hediye kartı</dt>
                  <dd>− {money(data.giftCardTotal, data.currencyCode)}</dd>
                </div>
              ) : null}
            </dl>

            <div className="mt-4 flex items-baseline justify-between border-t border-ink-200 pt-4">
              <span className="font-medium text-ink-700">Ödenecek</span>
              <span className="text-xl font-semibold text-ink-900">
                {money(data.amountDue, data.currencyCode)}
              </span>
            </div>

            <label className="mt-5 flex items-start gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-ink-300 text-brand-600"
                checked={acceptsTerms}
                onChange={(event) => setAcceptsTerms(event.target.checked)}
                required
              />
              <span>
                <Link to="/sayfa/mesafeli-satis-sozlesmesi" className="link" target="_blank">
                  Mesafeli satış sözleşmesi
                </Link>
                'ni okudum ve kabul ediyorum.
              </span>
            </label>

            <button
              type="submit"
              className="btn-primary mt-5 w-full flex items-center justify-center gap-2 py-3 text-sm font-semibold"
              disabled={!canSubmit || checkout.isPending}
            >
              {checkout.isPending ? <Spinner className="h-4 w-4" /> : null}
              {paymentMethod === 'CreditCard' ? (
                <>
                  <Lock className="h-4 w-4" />
                  <span>Güvenli Ödemeye Geç ({money(data.amountDue, data.currencyCode)})</span>
                </>
              ) : (
                <span>Siparişi Tamamla ({money(data.amountDue, data.currencyCode)})</span>
              )}
            </button>

            {!data.canCheckout ? (
              <p className="mt-3 text-xs text-red-600">
                Sepetinizde çözülmesi gereken sorunlar var. Lütfen sepet sayfasını kontrol edin.
              </p>
            ) : null}
          </div>
        </aside>
      </form>

      {/* PayTR Güvenli Ödeme Popup Modalı */}
      {isModalOpen && paytrResult?.paymentIframeUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-200">
          <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[94vh] border border-slate-200 animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-4 bg-slate-900 text-white border-b border-slate-800">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/20 text-emerald-400">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold tracking-tight text-white flex items-center gap-2">
                    PayTR Güvenli Ödeme
                    <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-medium text-emerald-300">
                      256-Bit SSL
                    </span>
                  </h3>
                  <p className="text-xs text-slate-400">Taksit seçenekleri ve kart bilgilerinizi güvenle giriniz.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                className="rounded-lg p-2 text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                title="Pencereyi Kapat"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body: iFrame */}
            <div className="relative flex-1 min-h-[580px] sm:min-h-[620px] bg-slate-50 flex flex-col items-center justify-center overflow-auto">
              {iframeLoading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/95 z-20">
                  <Spinner className="h-8 w-8 text-brand-600 mb-2" />
                  <p className="text-sm font-semibold text-slate-800">Güvenli Ödeme Ekranı Yükleniyor...</p>
                  <p className="text-xs text-slate-500 mt-1">Lütfen bekleyiniz, sayfa yenilenmeyecektir.</p>
                </div>
              )}
              <iframe
                id="paytriframe"
                src={paytrResult.paymentIframeUrl}
                frameBorder="0"
                scrolling="auto"
                className="w-full h-full min-h-[580px] sm:min-h-[620px] border-0"
                onLoad={() => {
                  setIframeLoading(false);
                  // @ts-ignore
                  if (typeof window !== 'undefined' && window.iFrameResize) {
                    // @ts-ignore
                    window.iFrameResize({}, '#paytriframe');
                  }
                }}
              />
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between px-5 py-3.5 bg-slate-50 border-t border-slate-200 text-xs text-slate-600">
              <span className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                🔒 BDDK Lisanslı Güvenli Ödeme Altyapısı
              </span>
              <button
                type="button"
                onClick={handleCloseModal}
                className="text-xs font-semibold text-rose-600 hover:text-rose-700 hover:underline px-3 py-1.5 rounded-lg hover:bg-rose-50 transition-colors"
              >
                İptal Et ve Sepete Dön
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AddressForm({
  value,
  onChange,
  idPrefix,
  corporate = true,
}: {
  value: AddressInputDto;
  onChange: (next: AddressInputDto) => void;
  idPrefix: string;
  corporate?: boolean;
}) {
  const set = <K extends keyof AddressInputDto>(key: K, next: AddressInputDto[K]) =>
    onChange({ ...value, [key]: next });

  // 1. Fetch cities from unified backend API (with fallback to TURKEY_CITIES)
  const { data: apiCities } = useQuery({
    queryKey: ['shipping', 'cities'],
    queryFn: shippingLocationsApi.getCities,
    staleTime: 1000 * 60 * 60,
  });
  const cities = useMemo(() => {
    if (apiCities && apiCities.length > 0) return apiCities;
    return TURKEY_CITIES;
  }, [apiCities]);

  const selectedCityObj = useMemo(() => {
    return cities.find((c) => c.name === value.city);
  }, [cities, value.city]);

  // 2. Fetch districts from unified backend API for selected city
  const { data: apiDistricts } = useQuery({
    queryKey: ['shipping', 'districts', selectedCityObj?.id],
    queryFn: () => shippingLocationsApi.getDistricts(selectedCityObj!.id),
    enabled: !!selectedCityObj?.id,
    staleTime: 1000 * 60 * 60,
  });
  const districts = useMemo(() => {
    if (apiDistricts && apiDistricts.length > 0) {
      return apiDistricts.map((d) => d.name);
    }
    return getDistrictsForCity(value.city);
  }, [apiDistricts, value.city]);

  const [neighborhoods, setNeighborhoods] = useState<{ value: string; label: string }[]>([]);
  const [loadingNeighborhoods, setLoadingNeighborhoods] = useState(false);

  useEffect(() => {
    if (!selectedCityObj || !value.district) {
      setNeighborhoods([]);
      return;
    }

    let isMounted = true;
    setLoadingNeighborhoods(true);
    shippingLocationsApi
      .getNeighborhoods(selectedCityObj.id, value.district)
      .then((items) => {
        if (!isMounted) return;
        setNeighborhoods(items.map((n) => ({ value: n.name, label: n.fullName || n.name })));
      })
      .catch(() => {
        if (!isMounted) return;
        setNeighborhoods([]);
      })
      .finally(() => {
        if (isMounted) setLoadingNeighborhoods(false);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedCityObj?.id, value.district]);

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label="Ad" htmlFor={`${idPrefix}-firstName`} required>
        <input
          id={`${idPrefix}-firstName`}
          className="field"
          value={value.firstName}
          onChange={(event) => set('firstName', event.target.value)}
        />
      </Field>
      <Field label="Soyad" htmlFor={`${idPrefix}-lastName`} required>
        <input
          id={`${idPrefix}-lastName`}
          className="field"
          value={value.lastName}
          onChange={(event) => set('lastName', event.target.value)}
        />
      </Field>
      <Field label="Telefon" htmlFor={`${idPrefix}-phone`} required>
        <input
          id={`${idPrefix}-phone`}
          type="tel"
          className="field"
          value={value.phone}
          onChange={(event) => set('phone', event.target.value)}
        />
      </Field>
      <Field label="Şirket" htmlFor={`${idPrefix}-company`}>
        <input
          id={`${idPrefix}-company`}
          className="field"
          value={value.company ?? ''}
          onChange={(event) => set('company', event.target.value)}
        />
      </Field>
      <Field label="İl" htmlFor={`${idPrefix}-city`} required>
        {value.countryCode === 'TR' ? (
          <SearchableSelect
            id={`${idPrefix}-city`}
            value={value.city}
            placeholder="İl Seçiniz..."
            searchPlaceholder="İl adı veya plaka ara (örn: 34 veya İstanbul)..."
            options={cities.map((c) => ({
              value: c.name,
              label: `${c.plate} - ${c.name}`,
            }))}
            onChange={(newCity) => {
              onChange({
                ...value,
                city: newCity,
                district: '',
                addressLine2: '',
              });
            }}
          />
        ) : (
          <input
            id={`${idPrefix}-city`}
            className="field"
            value={value.city}
            onChange={(event) => set('city', event.target.value)}
          />
        )}
      </Field>
      <Field label="İlçe" htmlFor={`${idPrefix}-district`} required>
        {value.countryCode === 'TR' ? (
          <SearchableSelect
            id={`${idPrefix}-district`}
            value={value.district}
            placeholder="İlçe Seçiniz..."
            searchPlaceholder="İlçe ara (örn: Kadıköy)..."
            options={districts.map((d) => ({
              value: d,
              label: d,
            }))}
            onChange={(newDistrict) => {
              onChange({
                ...value,
                district: newDistrict,
                addressLine2: '',
              });
            }}
            disabled={!value.city}
          />
        ) : (
          <input
            id={`${idPrefix}-district`}
            className="field"
            value={value.district}
            onChange={(event) => set('district', event.target.value)}
          />
        )}
      </Field>
      <Field label="Mahalle" htmlFor={`${idPrefix}-neighborhood`}>
        {value.countryCode === 'TR' && neighborhoods.length > 0 ? (
          <SearchableSelect
            id={`${idPrefix}-neighborhood`}
            value={value.addressLine2 ?? ''}
            placeholder="Mahalle Seçiniz..."
            searchPlaceholder="Mahalle ara..."
            isLoading={loadingNeighborhoods}
            options={neighborhoods}
            onChange={(newNeighborhood) => set('addressLine2', newNeighborhood)}
            disabled={!value.district}
          />
        ) : (
          <input
            id={`${idPrefix}-line2`}
            className="field"
            placeholder="Mahalle veya ek adres bilgisi"
            value={value.addressLine2 ?? ''}
            onChange={(event) => set('addressLine2', event.target.value)}
          />
        )}
      </Field>
      <Field label="Posta kodu" htmlFor={`${idPrefix}-postal`}>
        <input
          id={`${idPrefix}-postal`}
          className="field"
          placeholder="Örn: 34710"
          value={value.postalCode ?? ''}
          onChange={(event) => set('postalCode', event.target.value)}
        />
      </Field>
      <Field label="Açık Adres (Cadde, Sokak, Kapı No, Daire)" htmlFor={`${idPrefix}-line1`} required className="sm:col-span-2">
        <textarea
          id={`${idPrefix}-line1`}
          rows={2}
          className="field"
          placeholder="Örn: İstiklal Cad. Çiçek Pasajı No: 12 Daire: 4"
          value={value.addressLine1}
          onChange={(event) => set('addressLine1', event.target.value)}
        />
      </Field>
      <Field label="Ülke" htmlFor={`${idPrefix}-country`} required>
        <select
          id={`${idPrefix}-country`}
          className="field"
          value={value.countryCode}
          onChange={(event) => set('countryCode', event.target.value)}
        >
          <option value="TR">Türkiye</option>
          <option value="DE">Almanya</option>
          <option value="NL">Hollanda</option>
          <option value="GB">Birleşik Krallık</option>
          <option value="US">Amerika Birleşik Devletleri</option>
        </select>
      </Field>
      <Field label="Teslimat notu" htmlFor={`${idPrefix}-note`} className="sm:col-span-2">
        <input
          id={`${idPrefix}-note`}
          className="field"
          placeholder="Kapıcıya bırakılabilir, zil çalışmıyor vb."
          value={value.deliveryNote ?? ''}
          onChange={(event) => set('deliveryNote', event.target.value)}
        />
      </Field>

      {corporate ? (
        <>
          <label className="flex items-center gap-2 text-sm text-ink-700 sm:col-span-2">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-300 text-brand-600"
              checked={value.isCorporate}
              onChange={(event) => set('isCorporate', event.target.checked)}
            />
            Kurumsal fatura
          </label>

          {value.isCorporate ? (
            <>
              <Field label="Vergi dairesi" htmlFor={`${idPrefix}-taxOffice`}>
                <input
                  id={`${idPrefix}-taxOffice`}
                  className="field"
                  value={value.taxOffice ?? ''}
                  onChange={(event) => set('taxOffice', event.target.value)}
                />
              </Field>
              <Field label="Vergi numarası" htmlFor={`${idPrefix}-taxNumber`}>
                <input
                  id={`${idPrefix}-taxNumber`}
                  className="field"
                  value={value.taxNumber ?? ''}
                  onChange={(event) => set('taxNumber', event.target.value)}
                />
              </Field>
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
